import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { useStore } from 'react-redux';
import type { RootState } from '@/store';
import { registrarAcao } from '@/shared/utils/reportAction';
import { reportError } from '@/shared/utils/reportError';
import { FalhaNoUploadAssinado, uploadParaUrlAssinada } from '@/shared/utils/uploadParaUrlAssinada';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { buscarSimulacaoAtual, configuracaoAlteradaDetectada, estadoDaSimulacaoRecebido } from '../store/actions';
import { comandosDaSuperficie, FalhaDaSimulacao } from '../store/services';
import { selectSimulacaoAtual, selectSuperficieDaSimulacao } from '../store/selectors';
import type { EstadoSimulacao, SimulacaoComposerStatus } from '../types';
import type { EnvioDoAnexo, EnviarAnexo } from '../types/hookTypes';
import { validarAnexo } from '../utils/anexoDaSimulacao';
const statusDoEnvio = (envio: EnvioDoAnexo): SimulacaoComposerStatus | null => {
  if (envio.status === 'enviando') {
    return {
      text: `Enviando anexo… ${envio.progresso}%`,
      tone: 'neutral',
      canRetry: false
    };
  }
  if (envio.status === 'falhou') return {
    text: envio.mensagem,
    tone: 'error',
    canRetry: false
  };
  return null;
};
const MENSAGEM_GENERICA = 'Não foi possível enviar o anexo. Tente novamente.';
const podeAnexar = (simulacao: EstadoSimulacao | null): simulacao is EstadoSimulacao => simulacao?.status === 'ATIVA' && simulacao.aceitaAnexo;

/**
 * Recusa do core (ANEXO_INVALIDO, ANEXO_NAO_PERMITIDO) é desfecho previsto e vai para
 * `registrarAcao`; rede, storage e 5xx vão para `reportError`. Só ids, status e código.
 */
const registrarFalhaDoAnexo = (erro: unknown, simulacaoId: string) => {
  if (erro instanceof FalhaDaSimulacao && erro.erro.status >= 400 && erro.erro.status < 500) {
    registrarAcao('negociacaoSimulacao.anexoRecusado', {
      simulacaoId,
      status: erro.erro.status,
      codigo: erro.erro.codigo
    });
    return;
  }
  reportError(erro instanceof Error ? erro : new Error(MENSAGEM_GENERICA), {
    source: 'negociacaoSimulacao',
    acao: 'enviarAnexo',
    simulacaoId
  });
};
const mensagemDaFalha = (erro: unknown): string => {
  if (erro instanceof FalhaDaSimulacao || erro instanceof FalhaNoUploadAssinado) {
    return erro.message;
  }
  return MENSAGEM_GENERICA;
};

/**
 * Hook de efeito de navegador (exceção escrita da regra #1): o `File` não pode passar pelo Redux.
 * O caminho é o do CA-13: POST /anexos → PUT na URL assinada com os headers dela → POST da
 * confirmação, que entrega o anexo ao flow e devolve o estado. Recusado no cliente, nada sai.
 */
export function useEnviarAnexoDaSimulacao() {
  const $ = _c(20);
  const dispatch = useAppDispatch();
  const store = useStore();
  const simulacao = useAppSelector(selectSimulacaoAtual);
  const superficie = useAppSelector(selectSuperficieDaSimulacao);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      status: "ocioso"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [envio, setEnvio] = useState(t0);
  let t1;
  if ($[1] !== dispatch || $[2] !== store || $[3] !== superficie) {
    t1 = async (simulacaoId, arquivo) => {
      const escritorioId = store.getState().negociacaoSimulacao.escritorioId;
      const contextoVigente = () => {
        const atual = store.getState().negociacaoSimulacao;
        return atual.escritorioId === escritorioId && atual.simulacao?.id === simulacaoId;
      };
      const comandos = comandosDaSuperficie(superficie);
      const destino = await comandos.criarAnexo(simulacaoId, {
        nomeArquivo: arquivo.name,
        mimeType: arquivo.type,
        tamanhoBytes: arquivo.size
      }, crypto.randomUUID());
      if (!contextoVigente()) {
        return;
      }
      await uploadParaUrlAssinada(destino, arquivo, {
        onProgress: progresso => setEnvio({
          status: "enviando",
          progresso
        })
      });
      if (!contextoVigente()) {
        return;
      }
      const {
        estado
      } = await comandos.confirmarAnexo(simulacaoId, destino.anexoId, crypto.randomUUID());
      if (!contextoVigente()) {
        return;
      }
      dispatch(estadoDaSimulacaoRecebido(estado));
      registrarAcao("negociacaoSimulacao.anexoEnviado", {
        simulacaoId,
        anexoId: destino.anexoId,
        tamanhoBytes: arquivo.size
      });
    };
    $[1] = dispatch;
    $[2] = store;
    $[3] = superficie;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const subir = t1;
  let t2;
  if ($[5] !== dispatch || $[6] !== envio.status || $[7] !== simulacao || $[8] !== store || $[9] !== subir) {
    t2 = async arquivo_0 => {
      if (!podeAnexar(simulacao) || envio.status === "enviando") {
        return;
      }
      const validacao = validarAnexo(arquivo_0);
      if (!validacao.valido) {
        setEnvio({
          status: "falhou",
          mensagem: validacao.mensagem
        });
        return;
      }
      setEnvio({
        status: "enviando",
        progresso: 0
      });
      ;
      try {
        await subir(simulacao.id, arquivo_0);
        setEnvio({
          status: "ocioso"
        });
      } catch (t3) {
        const erro = t3;
        if (store.getState().negociacaoSimulacao.simulacao?.id !== simulacao.id) {
          setEnvio({
            status: "ocioso"
          });
          return;
        }
        registrarFalhaDoAnexo(erro, simulacao.id);
        setEnvio({
          status: "falhou",
          mensagem: mensagemDaFalha(erro)
        });
        if (erro instanceof FalhaDaSimulacao && erro.erro.codigo === "SIMULACAO_ENCERRADA") {
          dispatch(buscarSimulacaoAtual());
        }
        if (erro instanceof FalhaDaSimulacao && erro.erro.codigo === "CONFIGURACAO_ALTERADA") {
          dispatch(configuracaoAlteradaDetectada(simulacao.id));
          dispatch(buscarSimulacaoAtual());
        }
      }
    };
    $[5] = dispatch;
    $[6] = envio.status;
    $[7] = simulacao;
    $[8] = store;
    $[9] = subir;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const enviar = t2;
  let t3;
  if ($[11] !== envio.status) {
    t3 = () => {
      if (envio.status === "falhou") {
        setEnvio({
          status: "ocioso"
        });
      }
    };
    $[11] = envio.status;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const descartarFalha = t3;
  let t4;
  if ($[13] !== envio) {
    t4 = statusDoEnvio(envio);
    $[13] = envio;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const t5 = envio.status === "enviando";
  let t6;
  if ($[15] !== descartarFalha || $[16] !== enviar || $[17] !== t4 || $[18] !== t5) {
    t6 = {
      status: t4,
      enviando: t5,
      enviar,
      descartarFalha
    };
    $[15] = descartarFalha;
    $[16] = enviar;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
}